import Icon from '@expo/vector-icons/FontAwesome';
import { setStringAsync } from 'expo-clipboard';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { getAccessToken, getUser } from '@/stores/auth/services';
import { getCurrentRoute } from '@/navigations/RootNavigation';
import { useDialogBridge } from '@/hooks/useDialogBridge';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import colors from '@/config/colors';
import theme from '@/config/theme';
import { IS_WEB } from '@/utils/platform';

const noTherapistText =
  'Ihr Tablet ist noch nicht mit einem Therapeut:innen–Account verbunden. Bitte geben Sie diesen Code an Ihre:n Therapeut:in.';

const noSetsText =
  'Ihr:e Therapeut:in hat noch kein Übungsset für Sie ausgewählt';

interface TherapistNoticeModalProps {
  noTherapistNotice: string | boolean | null;
  syncPatientSettings: () => void | Promise<void>;
  setNoTherapistNotice: (value: boolean) => void;
  serverLogoutUser: (args: { jwt: string }) => Promise<unknown> | null;
  localLogoutUser: () => Promise<unknown>;
}

export function TherapistNoticeModal({
  noTherapistNotice,
  syncPatientSettings,
  setNoTherapistNotice,
  serverLogoutUser,
  localLogoutUser,
}: TherapistNoticeModalProps) {
  const [code, setCode] = useState('');
  const { showNotification } = useNotification();
  const currentRoute = getCurrentRoute()?.name;
  const isHomeRoute = currentRoute === 'Home';

  const isNoSets =
    typeof noTherapistNotice === 'string'
      ? noTherapistNotice.includes('Übungsset')
      : false;

  useEffect(() => {
    (async () => {
      if (isNoSets) {
        return;
      }
      if (!isHomeRoute) {
        return;
      }
      const user = await getUser();
      if (user) {
        setCode(user.code);
      }
    })();
  }, [isNoSets, currentRoute]);

  const copyCode = async () => {
    if (IS_WEB) {
      await navigator.clipboard.writeText(code);
    }
    await setStringAsync(code);
    showNotification({ type: 'info', title: 'Ihr Code wurde kopiert' });
  };

  useDialogBridge(
    Boolean(noTherapistNotice) && isHomeRoute,
    () => ({
      title: 'Download fehlgeschlagen',
      contentComponent: () => (
        <View style={styles.container}>
          <Text style={styles.contentText}>
            {isNoSets ? noSetsText : noTherapistText}
          </Text>
          {!isNoSets ? (
            <Pressable onPress={copyCode} style={styles.copyRow}>
              <Text style={styles.codeText}>{code}</Text>
              <Icon
                name="clipboard"
                color={colors.darkPurpleBlue}
                size={36}
                style={styles.copyIcon}
              />
            </Pressable>
          ) : null}
        </View>
      ),
      confirmAction: {
        label: 'wiederholen',
        onPress: async () => {
          await syncPatientSettings();
        },
      },
      cancelAction: {
        label: 'abmelden',
        onPress: async () => {
          setNoTherapistNotice(false);
          await serverLogoutUser({ jwt: (await getAccessToken()) as string });
          await localLogoutUser();
        },
      },
      showCancelButton: true,
    }),
    [code, isNoSets, noTherapistNotice, isHomeRoute],
  );

  return null;
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    alignItems: 'flex-start',
  },
  contentText: {
    fontSize: theme.fontSizes['4xl'],
    textAlign: 'left',
  },
  copyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 16,
  },
  codeText: {
    fontSize: theme.fontSizes['5xl'],
    fontWeight: '700',
    marginRight: 12,
  },
  copyIcon: {
    alignSelf: 'center',
  },
});
